import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { CLINIC, NAV_LINKS } from "../data/clinic";

const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => setOpen(false), [pathname]);

    return (
        <>
            <motion.header
                initial={{ y: -80, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
                    scrolled
                        ? "bg-[#060a07]/85 backdrop-blur-xl border-b border-emerald-500/15 shadow-[0_8px_32px_rgba(0,0,0,0.5)]"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <nav className="relative mx-auto flex max-w-7xl items-center justify-end px-4 py-4 sm:px-8 lg:px-16">
                    <div className="hidden items-center gap-8 lg:absolute lg:left-1/2 lg:flex lg:-translate-x-1/2">
                        {NAV_LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.testId}
                                className={({ isActive }) =>
                                    `relative text-[13px] font-medium tracking-[0.14em] uppercase transition-colors duration-300 ${
                                        isActive
                                            ? "text-emerald-300"
                                            : "text-emerald-100/60 hover:text-emerald-300"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {l.label}
                                        <span
                                            className={`absolute -bottom-1.5 left-0 h-px bg-emerald-400 transition-all duration-500 ${
                                                isActive ? "w-full" : "w-0"
                                            }`}
                                        />
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </div>

                    <div className="hidden items-center gap-3 lg:ml-auto lg:flex">
                        <a
                            href={CLINIC.phoneHref}
                            data-testid="nav-book-button"
                            className="rounded-full bg-emerald-500 px-5 py-2 text-[13px] font-semibold tracking-wide text-[#04120a] transition-all duration-300 hover:bg-emerald-400 hover:shadow-[0_0_24px_rgba(34,197,94,0.4)]"
                        >
                            Call to Book
                        </a>
                    </div>

                    <button
                        data-testid="nav-mobile-menu-button"
                        onClick={() => setOpen(!open)}
                        className="flex h-10 w-10 items-center justify-center rounded-full border border-emerald-500/30 text-emerald-200 lg:hidden"
                        aria-label="Toggle menu"
                    >
                        {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                    </button>
                </nav>
            </motion.header>

            <AnimatePresence>
                {open && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.4 }}
                        className="fixed inset-0 z-40 flex flex-col justify-center bg-[#060a07]/97 backdrop-blur-2xl lg:hidden"
                        data-testid="nav-mobile-overlay"
                    >
                        <div className="noise-overlay pointer-events-none absolute inset-0 opacity-30" />
                        <div className="relative flex flex-col gap-2 px-8">
                            {NAV_LINKS.map((l, i) => (
                                <motion.div
                                    key={l.to}
                                    initial={{ opacity: 0, y: 28 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.1 + i * 0.08, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
                                >
                                    <NavLink
                                        to={l.to}
                                        className={({ isActive }) =>
                                            `font-display block py-3 text-4xl font-light tracking-tight ${
                                                isActive ? "text-emerald-300 italic" : "text-emerald-50"
                                            }`
                                        }
                                    >
                                        {l.label}
                                    </NavLink>
                                </motion.div>
                            ))}
                            <motion.a
                                initial={{ opacity: 0, y: 28 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.45, duration: 0.6 }}
                                href={CLINIC.phoneHref}
                                className="mt-8 flex w-fit items-center gap-3 rounded-full bg-emerald-500 px-7 py-3.5 font-semibold text-[#04120a]"
                            >
                                <Phone className="h-4 w-4" /> Call to Book · {CLINIC.phoneDisplay}
                            </motion.a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default Navbar;